import React, { useState, useEffect, useCallback } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useGroupForm } from "../../hooks/useGroupForm";
import { descriptionPageSchema } from "../../Valdations/Schema/group.schema";
import FileUpload from "../../UploadFile/CustomActivity";
import styles from "./customActivityGroups.module.css";
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import DataTable from "../../shared/DataTable";
import { Button } from "../../components/ui/button";
import { CustomActivityAxiosInstance } from "../../Network/CustomActivity";
import { Datum } from "../../interface/CustomActivity";
import CodeMultiSelect from "../../component/CodeMultiSelect";
import type { CodeOption } from "../../component/SearchableCodeSelect";
import ActivityTypeSingleSelector from "../../component/ActivityTypeSelector";
import {
  ActivityType,
  ACTIVITY_TYPE_TO_CODE,
  CODE_TO_ACTIVITY_TYPE,
} from "../../component/ActivityTypeSelector";
import { AlertTriangle } from "lucide-react";
import ActivityDetailsDialog from "./ActivityDetailsDialog";
import { canRestoreOrHardDelete } from "../../interface/RoleHelpers";
import { isViewer } from "../../interface/RoleHelpers";

const CustomActivity: React.FC = () => {
  const [allItems, setAllItems] = useState<Datum[]>([]);
  const [codesByType, setCodesByType] = useState<{
    CPT: CodeOption[];
    HCPCS: CodeOption[];
    Dental: CodeOption[];
    Service: CodeOption[];
  }>({ CPT: [], HCPCS: [], Dental: [], Service: [] });
  const [selectedType, setSelectedType] = useState<ActivityType | undefined>(undefined);
  const [descriptions, setDescriptions] = useState<string>("");
  const [activeRowId, setActiveRowId] = useState<string | number | null>(null);
  const [medicalCptGroups, setMedicalCptGroups] = useState<CodeOption[]>([]);
  const [customCptGroups, setcustomCptGroups] = useState<CodeOption[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);

  // =====================================
  // Pagination / Search State (server-side)
  // =====================================
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [warningDialog, setWarningDialog] = useState<{
    open: boolean;
    type: "delete" | "toggle" | "update" | "hardDelete" | null;
    item: Datum | null;
    usageData: any;
    loadingUsage: boolean;
  }>({
    open: false,
    type: null,
    item: null,
    usageData: null,
    loadingUsage: false,
  });

  const [submitted, setSubmitted] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [CptDialogOpen, setCptDialogOpen] = useState(false);

  // =====================================
  // Advanced Search (server-side)
  // =====================================
  const [advancedSearchInput, setAdvancedSearchInput] = useState({
    groupName: "",
    activityCode: "",
    activityType: "",
    status: "",
    isSoftDeleted: "false",
  });
  const [advancedSearch, setAdvancedSearch] = useState({
    groupName: "",
    activityCode: "",
    activityType: "",
    status: "",
    isSoftDeleted: "false",
  });

  const { toast } = useToast();
  const { values, errors, setField, validateAll } = useGroupForm(descriptionPageSchema);

  // ─── currentOpenItem ───
  const currentOpenItem = allItems.find((item) => item.id === activeRowId);

  // ─── totalCount for ActivityDetailsDialog ───
  const totalCount = currentOpenItem
    ? (currentOpenItem.cptCodes?.length || 0) +
      (currentOpenItem.hecpcsCodes?.length || 0) +
      (currentOpenItem.dentalCodes?.length || 0) +
      (currentOpenItem.serviceCodes?.length || 0) +
      (currentOpenItem.medicalActivityGroups?.length || 0) +
      (currentOpenItem.customActivityGroups?.length || 0)
    : 0;

  const isAtLeastOneSelected = (): boolean => {
    return (
      codesByType.CPT.length +
        codesByType.HCPCS.length +
        codesByType.Dental.length +
        codesByType.Service.length >
      0
    );
  };

  const clear = () => {
    setSubmitted(false);
    setField("name", "");
    setDescriptions("");
    setSelectedType(undefined);
    setCodesByType({ CPT: [], HCPCS: [], Dental: [], Service: [] });
    setMedicalCptGroups([]);
    setcustomCptGroups([]);
    setIsActive(true);
  };

  const clearFilters = () => {
    setSearchInput("");
    setAdvancedSearchInput({
      groupName: "",
      activityCode: "",
      activityType: "",
      status: "",
      isSoftDeleted: "false",
    });
  };

  const getCodesState = () => {
    switch (selectedType) {
      case "CPT":
        return { value: codesByType.CPT, setter: (v: CodeOption[]) => setCodesByType((p) => ({ ...p, CPT: v })) };
      case "HCPCS":
        return { value: codesByType.HCPCS, setter: (v: CodeOption[]) => setCodesByType((p) => ({ ...p, HCPCS: v })) };
      case "Dental":
        return { value: codesByType.Dental, setter: (v: CodeOption[]) => setCodesByType((p) => ({ ...p, Dental: v })) };
      case "Service":
        return { value: codesByType.Service, setter: (v: CodeOption[]) => setCodesByType((p) => ({ ...p, Service: v })) };
      default:
        return { value: [], setter: () => {} };
    }
  };

  const handleOpenDialog = async (item: Datum) => {
    try {
      const response = await CustomActivityAxiosInstance.get(
        `custom-activity-group/${item.id}`,
        { params: { includeCodes: true } }
      );
      const fullData = response.data.data || response.data;
      setSelectedItem(fullData);
      setIsViewDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch group details:", error);
      toast({
        title: "Error",
        description: "Failed to load group details. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCloseDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedItem(null);
    setSubmitted(false);
    setShowAllLogs(false);
    clear();
  };

  const handleAdd = () => {
    setEditIndex(null);
    setField("name", "");
    setDescriptions("");
    setCodesByType({ CPT: [], HCPCS: [], Dental: [], Service: [] });
    setcustomCptGroups([]);
    setMedicalCptGroups([]);
    setIsDialogOpen(true);
  };

  const fetchCodesByType = useCallback(
    async (
      searchTerm: string,
      page = 1,
      pageSize = 10
    ): Promise<{ data: CodeOption[]; total: number }> => {
      
      const minCharsRequired = selectedType === "Service" ? 1 : 3;

      if (!selectedType || !searchTerm || searchTerm.trim().length < minCharsRequired) {
        return { data: [], total: 0 };
      }

      const typeCode = ACTIVITY_TYPE_TO_CODE[selectedType];

      try {
        const response = await CustomActivityAxiosInstance.get(
          "medical-code/activity",
          {
            params: { search: searchTerm, type: typeCode, page, limit: pageSize },
          }
        );

        const items = response.data?.data?.items ?? [];

        return {
          data: items.map((item: any) => ({
            value: item.code,
            label: `${item.code} - ${item.description}`,
          })),
          total: response.data?.data?.total ?? items.length,
        };
      } catch (error) {
        toast({
          title: "Error",
          description: getApiErrorMessage(error),
          variant: "destructive",
        });

        return { data: [], total: 0 };
      }
    },
    [selectedType, toast]
  );

  const fetchMedicalCodes = async (searchTerm: string): Promise<{ data: CodeOption[]; total: number }> => {
    if (!searchTerm || searchTerm.trim().length < 1) return { data: [], total: 0 };
    const response = await CustomActivityAxiosInstance.get("/medical-group/activity/", {
      params: { name: searchTerm, active: true },
    });
    const items = response.data?.data || [];
    return { data: items.map((g: any) => ({ value: g.name, label: g.name })), total: items.length };
  };

  const fetchCustomCodes = async (searchTerm: string): Promise<{ data: CodeOption[]; total: number }> => {
    if (!searchTerm || searchTerm.trim().length < 1) return { data: [], total: 0 };
    const response = await CustomActivityAxiosInstance.get(
      `custom-activity-group/search/${encodeURIComponent(searchTerm)}`,
    );
    const items: string[] = response.data?.data || [];
    const data = items.map((name) => ({ value: name, label: name }));
    return { data, total: data.length };
  };

  const getApiErrorMessage = (error: any): string => {
    if (!error) return "Unknown error";
    return (
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      error.message ||
      "An unexpected error occurred."
    );
  };

  const buildFilterParams = (): Record<string, any> => {
    const params: Record<string, any> = {
      includeCodes: true,
    };
    if (search) params.name = search;
    if (advancedSearch.groupName) params.name = advancedSearch.groupName;
    if (advancedSearch.status) params.isActive = advancedSearch.status === "Active";
    if (advancedSearch.isSoftDeleted !== "") {
      params.isSoftDeleted = advancedSearch.isSoftDeleted === "true";
    }
    if (advancedSearch.activityType) {
      params.type = ACTIVITY_TYPE_TO_CODE[advancedSearch.activityType as ActivityType];
    }
    if (advancedSearch.activityCode) {
      params.Activities = advancedSearch.activityCode;
    }
    return params;
  };

  const hasActiveFilters = Boolean(
    search ||
      advancedSearch.groupName ||
      advancedSearch.activityCode ||
      advancedSearch.activityType ||
      advancedSearch.status ||
      advancedSearch.isSoftDeleted === "true"
  );

  // =====================================
  // Fetch Items
  // =====================================
  const fetchItems = async () => {
    try {
      const params: Record<string, any> = {
        page,
        limit,
        ...buildFilterParams(),
      };

      const res = await CustomActivityAxiosInstance.get("custom-activity-group", {
        params,
      });

      const responseBody = res.data;
      let items = responseBody?.data ?? [];

      if (!Array.isArray(items)) {
        items = items?.items ?? [];
      }

      setAllItems(items);
      setTotalPages(responseBody?.totalPages ?? responseBody?.data?.totalPages ?? 1);
      setTotalItems(responseBody?.total ?? responseBody?.data?.total ?? items.length);

      const pages = responseBody?.totalPages ?? responseBody?.data?.totalPages ?? 1;
      if (page > pages) {
        setPage(Math.max(1, pages));
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    } 
  };

  const addItem = async (payload: any) => {
    try {
      const response = await CustomActivityAxiosInstance.post("custom-activity-group", payload);
      if (response.data?.success) {
        fetchItems();
        setIsDialogOpen(false);
        clear();
        toast({ variant: "success", title: "Success", description: "New custom activity group added successfully." });
      }
    } catch (error: any) {
      toast({ title: "Error", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!validateAll()) return;
    if (!selectedType) {
      toast({ title: "Error", description: "Activity Type is Required.", variant: "destructive" });
      return;
    }
    if (!isAtLeastOneSelected()) {
      toast({ title: "Error", description: "At least one activity code is required.", variant: "destructive" });
      return;
    }
    await addItem({
      name: values.name.trim(),
      description: descriptions.trim(),
      activityType: ACTIVITY_TYPE_TO_CODE ? [ACTIVITY_TYPE_TO_CODE[selectedType]] : [],
      isActive,
      cptCodes: codesByType.CPT.map((c) => c.value),
      hecpcsCodes: codesByType.HCPCS.map((c) => c.value),
      dentalCodes: codesByType.Dental.map((c) => c.value),
      serviceCodes: codesByType.Service.map((c) => c.value),
      medicalActivityNames: medicalCptGroups.map((c) => c.value),
      customActivityNames: customCptGroups.map((c) => c.value),
    });
  };

  const handleEdit = (item: Datum) => {
    setField("name", item.name);
    setDescriptions(item.description);
    setIsActive(item.isActive);

    const backendTypes = item.activityType ?? [];
    setSelectedType(
      backendTypes.length > 0 ? CODE_TO_ACTIVITY_TYPE[backendTypes[0].toString()] : undefined,
    );

    setMedicalCptGroups(
      (item.medicalActivityGroups || []).map((obj) => ({
        value: obj.name,
        label: obj.name,
        isActive: obj.isActive,
      })),
    );

    setcustomCptGroups(
      (item.customActivityGroups || []).map(
        (obj: { id: number | string; name: string; isActive: boolean; isSoftDeleted: boolean }) => ({
          value: obj.name,
          label: obj.name,
          isActive: obj.isActive,
          isSoftDeleted: obj.isSoftDeleted ?? false,
        })
      )
    );
    setCodesByType({
      CPT: (item.cptCodes || []).map((code) => ({ value: code, label: code })),
      HCPCS: (item.hecpcsCodes || []).map((code) => ({ value: code, label: code })),
      Dental: (item.dentalCodes || []).map((code) => ({ value: code, label: code })),
      Service: (item.serviceCodes || []).map((code) => ({ value: code, label: code })),
    });

    setEditIndex(item.id);
    setIsDialogOpen(true);
  };

  const updateItem = async (id: string | number) => {
    setSubmitted(true);
    if (!validateAll()) return;
    if (!selectedType) {
      toast({ title: "Error", description: "Activity type is required.", variant: "destructive" });
      return;
    }
    if (!isAtLeastOneSelected()) {
      toast({ title: "Error", description: "At least one activity code is required.", variant: "destructive" });
      return;
    }

    const inactiveMedical = medicalCptGroups
      .filter((g: any) => g.isActive === false)
      .map((g: any) => ({ label: g.label, groupType: "Medical" }));

    const inactiveCustom = customCptGroups
      .filter((g: any) => g.isActive === false || g.isSoftDeleted === true)
      .map((g: any) => ({
        label: g.label,
        groupType: "Custom",
        isActive: g.isActive,
        isSoftDeleted: g.isSoftDeleted ?? false,
      }));

    const allInactive = [...inactiveMedical, ...inactiveCustom];

    if (allInactive.length > 0) {
      setWarningDialog({
        open: true,
        type: "update",
        item: { id, allInactive } as any,
        usageData: null,
        loadingUsage: false,
      });
      return;
    }

    executeActivityUpdate(id);
  };

  const executeActivityUpdate = async (id: string | number) => {
    try {
      const payload = {
        name: values.name.trim(),
        description: descriptions.trim(),
        activityType: [ACTIVITY_TYPE_TO_CODE[selectedType!]],
        isActive,
        cptCodes: codesByType.CPT.map((c) => c.value),
        hecpcsCodes: codesByType.HCPCS.map((c) => c.value),
        dentalCodes: codesByType.Dental.map((c) => c.value),
        serviceCodes: codesByType.Service.map((c) => c.value),
        medicalActivityNames: medicalCptGroups.map((c) => c.label),
        customActivityNames: customCptGroups.map((c) => c.label),
      };

      const response = await CustomActivityAxiosInstance.put(`custom-activity-group/${id}`, payload);
      if (response.data?.success) {
        fetchItems();
        setIsDialogOpen(false);
        clear();
        toast({ variant: "success", title: "Success", description: "Custom activity group updated successfully." });
      }
    } catch (error: any) {
      toast({ title: "Error", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const removeItem = async (id: string | number): Promise<void> => {
    const item = allItems.find((i) => i.id === id) ?? null;
    if (!item) return;

    try {
      const res = await CustomActivityAxiosInstance.get(`custom-activity-group/usage/${id}`);
      const usageData = res.data.data;
      setWarningDialog({ open: true, type: "delete", item, usageData, loadingUsage: false });
    } catch (error) {
      console.error("Error fetching usage for delete:", error);
      setWarningDialog({
        open: true,
        type: "delete",
        item,
        usageData: { isUsed: false, totalUsageCount: 0 },
        loadingUsage: false,
      });
    }
  };

  const toggleActive = async (row: Datum) => {
    if (row.isSoftDeleted) return;
    if (row.isActive) {
      try {
        const res = await CustomActivityAxiosInstance.get(`custom-activity-group/usage/${row.id}`);
        const usageData = res.data.data;

        if (!usageData.isUsed || usageData.totalUsageCount === 0) {
          confirmToggle(row);
        } else {
          setWarningDialog({ open: true, type: "toggle", item: row, usageData, loadingUsage: false });
        }
      } catch (error) {
        console.error("Error fetching usage:", error);
      }
    } else {
      confirmToggle(row);
    }
  };

  const confirmDelete = async (id: string | number): Promise<void> => {
    try {
      await CustomActivityAxiosInstance.delete(`custom-activity-group/${id}/soft-delete`);
      fetchItems();
      toast({ variant: "success", title: "Success", description: "Group deleted successfully." });
    } catch (error: any) {
      toast({ title: "Error", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const hardDeleteItem = async (id: string | number): Promise<void> => {
    try {
      await CustomActivityAxiosInstance.delete(`custom-activity-group/${id}`);
      await fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "Group permanently deleted.",
      });
    } catch (error: any) {
      toast({
         title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      await CustomActivityAxiosInstance.patch(`custom-activity-group/${id}/restore`);
      await fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "Group restored successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const confirmToggle = async (row: Datum) => {
    const medicalNames = (row.medicalActivityGroups || []).map((g) => g.name);
    const customNames = (row.customActivityGroups || []).map((g) => g.name);

    const payload = {
      name: row.name,
      description: row.description,
      cptCodes: row.cptCodes || [],
      hecpcsCodes: row.hecpcsCodes || [],
      dentalCodes: row.dentalCodes || [],
      serviceCodes: row.serviceCodes || [],
      medicalActivityNames: medicalNames,
      customActivityNames: customNames,
      isActive: !row.isActive,
      activityType: row.activityType || [],
    };

    try {
      await CustomActivityAxiosInstance.put(`custom-activity-group/${row.id}`, payload);

      toast({
        variant: "success",
        title: "Success",
        description: `Status updated to ${!row.isActive ? "active" : "inactive"}`,
      });

      setAllItems((prev) =>
        prev.map((item) => {
          if (item.id === row.id) return { ...item, isActive: !item.isActive };

          const updatedMedicalGroups = item.medicalActivityGroups?.map((g) =>
            String(g.id) === String(row.id) ? { ...g, isActive: !row.isActive } : g,
          );
          const updatedCustomGroups = item.customActivityGroups?.map((g) =>
            String(g.id) === String(row.id) ? { ...g, isActive: !row.isActive } : g,
          );

          return { ...item, medicalActivityGroups: updatedMedicalGroups, customActivityGroups: updatedCustomGroups };
        }),
      );
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const formatText = (text: string) => {
    if (!text) return "";
    return text
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getCreatorName = (item: any): string | null => {
    if (!item) return null;
    if (item.createdBy) {
      const match = item.auditLog?.find((log: any) => log.actorId === item.createdBy);
      if (match) return match.actorName;
      return item.createdBy;
    }
    return null;
  };

  // =====================================
  // Debounce
  // =====================================
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      const cleanedActivityCode =
        advancedSearchInput.activityCode.trim().length >= 3
          ? advancedSearchInput.activityCode.trim()
          : "";

      setAdvancedSearch({
        groupName: advancedSearchInput.groupName.trim(),
        activityCode: cleanedActivityCode,
        activityType: advancedSearchInput.activityType,
        status: advancedSearchInput.status,
        isSoftDeleted: advancedSearchInput.isSoftDeleted,
      });
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [advancedSearchInput]);

  useEffect(() => {
    fetchItems();
  }, [page, limit, search, advancedSearch]);

  const ACTIVITY_TYPE_LABELS: Record<number, string> = { 3: "CPT", 4: "HCPCS", 6: "Dental", 8: "Service" };
  const getActivityTypeLabel = (value: string | number | undefined): string => {
    if (value === undefined || value === null) return "";
    return ACTIVITY_TYPE_LABELS[Number(value)] || String(value);
  };

  // =====================================
  // Download Function (Backend Export)
  // =====================================
  const handleDownload = async (item: Datum) => {
    if (isViewer()) {
      toast({
        title: "Error",
        description: "Access denied. Viewers are only allowed to view the system",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await CustomActivityAxiosInstance.get(`custom-activity-group/${item.id}/export`, {
        responseType: 'arraybuffer',
      });

      const data = new Uint8Array(response.data);
      const wb = XLSX.read(data, { type: 'array' });
      const wsName = "Custom Activity Groups";
      const ws = wb.Sheets[wsName];

      if (ws) {
        ws['A2'] = { t: 's', v: item.name ?? "" };
        ws['B2'] = { t: 's', v: item.description ?? "" };
        ws['C2'] = { t: 's', v: Array.isArray(item.activityType) ? item.activityType.map(getActivityTypeLabel).join(",") : getActivityTypeLabel(item.activityType) };
        ws['D2'] = { t: 's', v: (item.cptCodes ?? []).join(",") };
        ws['E2'] = { t: 's', v: (item.hecpcsCodes ?? []).join(",") };
        ws['F2'] = { t: 's', v: (item.dentalCodes ?? []).join(",") };
        ws['G2'] = { t: 's', v: (item.serviceCodes ?? []).join(",") };
        ws['H2'] = { t: 's', v: (item.medicalActivityGroups ?? []).map((g) => g.name).join(",") };
        ws['I2'] = { t: 's', v: (item.customActivityGroups ?? []).map((g) => g.name).join(",") };
        ws['!ref'] = "A1:I2";
      }

      XLSX.writeFile(wb, `${item.name || "Custom_Activity_Group"}_Export.xlsx`);

      toast({
        title: "Success",
        description: "Exported template with data successfully. Ready to re-upload!",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to generate export file.",
        variant: "destructive",
      });
    }
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query || !text) return text;
    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return (
      <span className="inline-flex flex-nowrap items-baseline">
        {parts.map((part, index) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark
              key={index}
              className="bg-amber-200 text-amber-900 font-bold p-0 m-0 leading-none inline"
              style={{ backgroundColor: "#fde68a", display: "inline", verticalAlign: "baseline" }}
            >
              {part}
            </mark>
          ) : (
            <span key={index} style={{ verticalAlign: "baseline" }}>
              {part}
            </span>
          ),
        )}
      </span>
    );
  };

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          data-testid="customActivity-table-header-name-sort-btn"
        >
          Name
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
            ? " ↓"
            : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div
            data-testid={`customActivity-table-cell-name-${row.index}`}
            className={`text-center ${item.isSoftDeleted ? "line-through text-slate-400" : ""}`}
          >
            {row.getValue("name")}
          </div>
        );
      },
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          data-testid="customActivity-table-header-description-sort-btn"
        >
          Description
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
            ? " ↓"
            : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div
            data-testid={`customActivity-table-cell-description-${row.index}`}
            className={`text-center ${
              item.isSoftDeleted ? "text-slate-400" : ""
            }`}
          >
            {row.getValue("description")}
          </div>
        );
      },
    },
    {
      id: "activities",
      accessorFn: (row: Datum) => [
        ...(row.cptCodes || []),
        ...(row.hecpcsCodes || []),
        ...(row.dentalCodes || []),
        ...(row.serviceCodes || []),
        ...(row.medicalActivityGroups || []).map((g) => g.name),
        ...(row.customActivityGroups || []).map((g) => g.name),
      ],
      header: () => (
        <div className="w-full font-bold text-black justify-center flex normal-case">Activities</div>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const isRowSoftDeleted = item.isSoftDeleted;

        const allData = [
          ...(item.cptCodes || []).map((c) => ({ label: c, type: "SINGLE_ACTIVITY", sub: "CPT", isActive: true, isSoftDeleted: false })),
          ...(item.hecpcsCodes || []).map((c) => ({ label: c, type: "SINGLE_ACTIVITY", sub: "HCPCS", isActive: true, isSoftDeleted: false })),
          ...(item.dentalCodes || []).map((c) => ({ label: c, type: "SINGLE_ACTIVITY", sub: "DENTAL", isActive: true, isSoftDeleted: false })),
          ...(item.serviceCodes || []).map((c) => ({
            label: c,
            type: "SINGLE_ACTIVITY",
            sub: "SERVICE",
            isActive: true,
            isSoftDeleted: false
          })),
          ...(item.medicalActivityGroups || []).map((g) => ({
            label: g.name,
            type: "MEDICAL_GROUP",
            sub: "",
            isActive: g.isActive,
            isSoftDeleted: false
          })),
          ...(item.customActivityGroups || []).map((g) => ({
            label: g.name,
            type: "CUSTOM_GROUP",
            sub: "",
            isActive: g.isActive,
            isSoftDeleted: g.isSoftDeleted || false
          })),
        ];

        if (allData.length === 0)
          return <span className="text-slate-300 text-center block font-medium" data-testid={`customActivity-table-cell-activities-empty-${row.index}`}>—</span>;

        const query = (advancedSearch.activityCode || search || "").toLowerCase().trim();
        const filtered = query ? allData.filter((i) => i.label.toLowerCase().includes(query)) : allData;

        const display = filtered.slice(0, 2);
        const extra = filtered.length - 2;

        const typeStyles: Record<string, string> = {
          SINGLE_ACTIVITY: "bg-indigo-50 border-indigo-100 text-indigo-600 ring-1 ring-indigo-200/50",
          MEDICAL_GROUP: "bg-emerald-50 border-emerald-100 text-emerald-600 ring-1 ring-emerald-200/50",
          CUSTOM_GROUP: "bg-violet-50 border-violet-100 text-violet-600 ring-1 violet-200/50",
        };

        return (
          <div
            data-testid={`customActivity-table-cell-activities-${row.index}`}
            className={`flex flex-col items-center gap-1.5 py-1 transition-all
              ${isRowSoftDeleted ? "opacity-40 pointer-events-none" : ""}
            `}
          >
            <div className="flex flex-wrap justify-center items-center gap-2">
              {display.map((act, idx) => {
                const isItemDisabled = !act.isActive || act.isSoftDeleted;

                return (
                  <div
                    key={idx}
                    data-testid={`customActivity-table-cell-activity-badge-${row.index}-${idx}`}
                    className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 border transition-all duration-200 shrink-0
                      ${
                        isItemDisabled
                          ? "opacity-50 grayscale border-dashed bg-slate-50 text-slate-400"
                          : query && act.label.toLowerCase().includes(query)
                          ? "bg-amber-50 border-amber-400 text-amber-900 ring-1 ring-amber-200"
                          : typeStyles[act.type]
                      }
                    `}
                  >
                    <i
                      className={`fa-solid ${
                        act.isSoftDeleted
                          ? "fa-trash-can text-red-400"
                          : act.type.includes("GROUP")
                          ? "fa-layer-group"
                          : "fa-tag"
                      } text-[9px] opacity-70`}
                    ></i>

                    <span className="text-[11px] font-semibold tracking-tight flex items-center gap-1">
                      {act.sub && <span className="text-[9px] opacity-60 font-bold">({act.sub})</span>}
                      <span className="inline-block whitespace-nowrap">{highlightMatch(act.label, query)}</span>
                    </span>

                    {act.isSoftDeleted && (
                      <span className="text-[8px] text-red-500 font-bold px-0.5 bg-red-50 rounded">Deleted</span>
                    )}
                    {!act.isActive && !act.isSoftDeleted && (
                      <i className="fa-solid fa-eye-slash text-[8px] opacity-60"></i>
                    )}
                  </div>
                );
              })}

              {extra > 0 && (
                <button
                  onClick={() => {
                    setActiveRowId(item.id);
                    setCptDialogOpen(true);
                  }}
                  disabled={isRowSoftDeleted}
                  data-testid={`customActivity-table-cell-more-btn-${row.index}`}
                  className="px-2 py-1 rounded-md text-[10px] font-bold bg-white border border-slate-200 text-slate-500 hover:bg-slate-900 hover:text-white transition-all shadow-sm active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="fa-solid fa-plus mr-1 text-[8px]"></i>
                  {extra} more
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "isActive",
      header: () => (
        <div className="w-full font-bold text-black text-center normal-case">
          Status
        </div>
      ),
      cell: ({ row }) => {
        const item = row.original;

        if (item.isSoftDeleted) {
          return (
            <div className="w-full flex justify-center" data-testid={`customActivity-table-cell-status-disabled-${row.index}`}>
              <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 opacity-40 cursor-not-allowed">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
              </div>
            </div>
          );
        }
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => toggleActive(item)}
              data-testid={`customActivity-table-cell-status-toggle-${row.index}`}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                item.isActive ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  item.isActive ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        );
      },
    },

    {
      id: "view",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black text-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          data-testid="customActivity-table-header-view-sort-btn"
        >
          View
          {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center py-2">
            <button
              onClick={() => handleOpenDialog(item)}
              data-testid={`customActivity-table-cell-view-btn-${row.index}`}
              className="text-blue-600 hover:text-blue-800"
              title="View Details"
            >
              <i className="fa-solid fa-eye text-lg"></i>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div data-testid="customActivity-page">
      {/* Header */}
      <div className={styles.container} data-testid="customActivity-page-container">
        <h5 className={styles.title} data-testid="customActivity-page-title">
          <span className={styles.dialogIcon} data-testid="customActivity-page-title-icon">
            <i className="fa-solid fa-folder-plus"></i>
          </span>
          Custom Activity Grouping
        </h5>
        <FileUpload
          fetchItems={fetchItems}
          totalItems={totalItems}
          hasFilters={hasActiveFilters}
          exportParams={buildFilterParams()}
        />
      </div>

      {/* Advanced Search */}
      <div
        className={`${styles["compact-search-container"]} ${styles["mb-6"]}`}
        data-testid="customActivity-page-search-container"
      >
        <div className={styles["search-wrapper"]}>
          <div className={styles["search-header-compact"]}>
            <div className={styles["header-info"]} data-testid="customActivity-page-search-header-info">
              <i className="fa-solid fa-filter"></i>
              <span>Search Filters</span>
            </div>
            <button
              onClick={clearFilters}
              className={styles["btn-clear-compact"]}
              data-testid="customActivity-page-search-reset-btn"
            >
              <i className="fa-solid fa-rotate-left"></i>
              Reset
            </button>
          </div>

          <div
            className={styles["search-fields-compact"]}
            style={{ gridTemplateColumns: "repeat(5, 1fr)", gap: "1rem" }}
          >
            {/* Group Name */}
            <div className={styles["field-compact"]} data-testid="customActivity-page-search-groupname-field">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-layer-group"></i> Group Name
              </label>
              <div className={styles["input-container-compact"]}>
                <input
                  type="text"
                  placeholder="Search groups..."
                  value={advancedSearchInput.groupName}
                  onChange={(e) => setAdvancedSearchInput({ ...advancedSearchInput, groupName: e.target.value })}
                  data-testid="customActivity-page-search-groupname-input"
                  className={styles["input-compact"]}
                />
                {advancedSearchInput.groupName && (
                  <button
                    onClick={() => setAdvancedSearchInput({ ...advancedSearchInput, groupName: "" })}
                    data-testid="customActivity-page-search-groupname-clear-btn"
                    className={styles["clear-btn-compact"]}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Activity Code */}
            <div className={styles["field-compact"]} data-testid="customActivity-page-search-activitycode-field">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-code"></i> Activities
              </label>
              <div className={styles["input-container-compact"]}>
                <input
                  type="text"
                  placeholder="Min 3 characters..."
                  value={advancedSearchInput.activityCode}
                  onChange={(e) => setAdvancedSearchInput({ ...advancedSearchInput, activityCode: e.target.value })}
                  data-testid="customActivity-page-search-activitycode-input"
                  className={`${styles["input-compact"]} ${
                    advancedSearchInput.activityCode.trim().length > 0 && advancedSearchInput.activityCode.trim().length < 3
                      ? styles["input-warning-compact"]
                      : ""
                  }`}
                />
                {advancedSearchInput.activityCode && (
                  <button
                    onClick={() => setAdvancedSearchInput({ ...advancedSearchInput, activityCode: "" })}
                    data-testid="customActivity-page-search-activitycode-clear-btn"
                    className={styles["clear-btn-compact"]}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Activity Type */}
            <div className={styles["field-compact"]} data-testid="customActivity-page-search-activitytype-field">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-tag"></i> Activity Type
              </label>
              <div className={styles["select-container-compact"]}>
                <select
                  value={advancedSearchInput.activityType}
                  onChange={(e) => setAdvancedSearchInput({ ...advancedSearchInput, activityType: e.target.value })}
                  data-testid="customActivity-page-search-activitytype-select"
                  className={styles["select-compact"]}
                >
                  <option value="">All Types</option>
                  <option value="CPT">CPT</option>
                  <option value="HCPCS">HCPCS</option>
                  <option value="Dental">Dental</option>
                  <option value="Service">Service</option>
                </select>
              </div>
            </div>

            {/* Status */}
            <div className={styles["field-compact"]} data-testid="customActivity-page-search-status-field">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-toggle-on"></i> Status
              </label>
              <div className={styles["select-container-compact"]}>
                <select
                  value={advancedSearchInput.status}
                  onChange={(e) => setAdvancedSearchInput({ ...advancedSearchInput, status: e.target.value })}
                  data-testid="customActivity-page-search-status-select"
                  className={styles["select-compact"]}
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Deleted Groups */}
            <div
              className={styles["field-compact"]}
              data-testid="customActivity-page-search-soft-deleted-field"
            >
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-trash-can"></i>
                Deleted Groups
              </label>
              <button
                type="button"
                data-testid="customActivity-page-search-soft-deleted-btn"
                onClick={() =>
                  setAdvancedSearchInput({
                    ...advancedSearchInput,
                    isSoftDeleted: advancedSearchInput.isSoftDeleted === "true" ? "false" : "true",
                  })
                }
                className={`${styles["input-compact"]} w-full flex items-center justify-center gap-2 cursor-pointer select-none transition-all ${
                  advancedSearchInput.isSoftDeleted === "true"
                    ? "!bg-red-500 !border-red-600 !text-white font-semibold"
                    : "hover:bg-slate-50 text-slate-600"
                }`}
              >
                <i
                  className={`fa-solid ${
                    advancedSearchInput.isSoftDeleted === "true" ? "fa-rotate-left" : "fa-eye-slash"
                  } text-xs`}
                ></i>
                <span className="text-xs">
                  {advancedSearchInput.isSoftDeleted === "true" ? "Back to List" : "Show "}
                </span>
              </button>
            </div>
          </div>

          <div className={styles["stats-compact"]} data-testid="customActivity-page-search-stats">
            <i className="fa-solid fa-chart-bar"></i>
            <span className={styles["stats-text-compact"]}>
              Showing <strong>{allItems.length}</strong> of <strong>{totalItems}</strong> groups
            </span>
            {(advancedSearch.groupName ||
              advancedSearch.activityCode ||
              advancedSearch.activityType ||
              advancedSearch.status ||
              advancedSearch.isSoftDeleted == "true") && (
              <span
                className={styles["badge-filtered-compact"]}
                data-testid="customActivity-page-search-filtered-badge"
              >
                <i className="fa-solid fa-filter"></i> Filtered
              </span>
            )}
          </div>
        </div>
      </div>

      {/* View Dialog */}
      {isViewDialogOpen && selectedItem && (
        <dialog className={styles["modern-dialog"]} open data-testid="customActivity-page-dialog">
          <div className={styles["dialog-container"]} data-testid="customActivity-page-dialog-container">
            <div className={styles["dialog-header"]} data-testid="customActivity-page-dialog-header">
              <div className={styles["header-content"]}>
                <div className={styles["header-icon"]} data-testid="customActivity-page-dialog-header-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <div>
                  <h2 className={styles["dialog-title"]} data-testid="customActivity-page-dialog-title">
                    Group Details
                  </h2>
                  <p className={styles["dialog-subtitle"]} data-testid="customActivity-page-dialog-subtitle">
                    {selectedItem.name}
                  </p>
                </div>
              </div>
              <button
                className={styles["close-btn"]}
                onClick={handleCloseDialog}
                data-testid="customActivity-page-dialog-close-btn"
              >
                ✕
              </button>
            </div>

            <div className={styles["dialog-body"]} data-testid="customActivity-page-dialog-body">
              <section className={styles.section} data-testid="customActivity-page-dialog-basic-info">
                <h3 className={styles["section-title"]}>Basic Information</h3>
                <div className={styles["info-group"]}>
                  <div className={styles["info-row"]} data-testid="customActivity-page-dialog-row-id">
                    <span className={styles["row-label"]}>Group ID</span>
                    <span className={styles["info-text"]} data-testid="customActivity-page-dialog-text-id">
                      {selectedItem
                        ? (page - 1) * limit + allItems.findIndex((i) => i.id === selectedItem.id) + 1
                        : ""}
                    </span>
                  </div>
                  <div className={styles["info-row"]} data-testid="customActivity-page-dialog-row-name">
                    <span className={styles["row-label"]}>Group Name</span>
                    <span className={styles["info-text"]} data-testid="customActivity-page-dialog-text-name">
                      {selectedItem.name}
                    </span>
                  </div>
                  <div className={styles["info-row"]} data-testid="customActivity-page-dialog-row-description">
                    <span className={styles["row-label"]}>Description</span>
                    <span className={styles["info-text"]} data-testid="customActivity-page-dialog-text-description">
                      {selectedItem.description}
                    </span>
                  </div>

                  {/* Medical Activity Groups */}
                  <div className={styles["info-row"]} data-testid="customActivity-page-dialog-row-medical-groups">
                    <span className={styles["row-label"]}>Medical Activity Groups</span>
                    <div className="flex flex-wrap gap-3">
                      {selectedItem.medicalActivityGroups?.length
                        ? selectedItem.medicalActivityGroups.map((group, idx) => (
                            <div
                              key={idx}
                              data-testid={`customActivity-page-dialog-med-group-${idx}`}
                              className={`${styles["group-badge-v2"]} ${
                                !group.isActive ? styles["badge-inactive"] : styles["badge-active"]
                              }`}
                            >
                              {!group.isActive && (
                                <i className="fa-solid fa-eye-slash mr-2 text-xs"></i>
                              )}
                              <span className={`${styles["group-name"]} ${!group.isActive ? "flex-1" : ""} `}>
                                {group.name}
                              </span>
                              {!group.isActive && (
                                <span className={`${styles["status-text-mini"]} ml-auto`}>Inactive</span>
                              )}
                            </div>
                          ))
                        : "—"}
                    </div>
                  </div>

                  {/* Custom Activity Groups */}
                  <div className={styles["info-row"]} data-testid="customActivity-page-dialog-row-custom-groups">
                    <span className={styles["row-label"]}>
                      Custom Activity Group
                    </span>
                    <div className="flex flex-wrap gap-3">
                      {selectedItem.customActivityGroups?.length
                        ? selectedItem.customActivityGroups.map((group, idx) => {
                            const softDeleteClasses = group.isSoftDeleted
                              ? "opacity-35 blur-[0.4px] line-through decoration-red-400 border-dashed bg-red-50 text-red-500"
                              : !group.isActive
                              ? styles["badge-inactive"]
                              : styles["badge-active"];

                            return (
                              <div
                                key={idx}
                                data-testid={`customActivity-page-dialog-custom-group-${idx}`}
                                className={`${styles["group-badge-v2"]} ${softDeleteClasses}`}
                              >
                                {group.isSoftDeleted ? (
                                  <i className="fa-solid fa-trash-can mr-2 text-xs text-red-500"></i>
                                ) : !group.isActive ? (
                                  <i className="fa-solid fa-eye-slash mr-2 text-xs"></i>
                                ) : null}

                                <span className={styles["group-name"]}>
                                  {group.name}
                                </span>

                                {group.isSoftDeleted ? (
                                  <span className="text-[9px] font-bold bg-red-100 text-red-600 px-1 rounded ml-1.5 tracking-wide">
                                    Deleted
                                  </span>
                                ) : !group.isActive ? (
                                  <span className={styles["status-text-mini"]}>
                                    Inactive
                                  </span>
                                ) : null}
                              </div>
                            );
                          })
                        : "—"}
                    </div>
                  </div>

                  <div className={styles["info-row"]} data-testid="customActivity-page-dialog-row-status">
                    <span className={styles["row-label"]}>Status</span>
                    <div
                      className={`${styles["status-badge-inline"]} ${
                        selectedItem.isActive ? styles.active : styles.inactive
                      }`}
                      data-testid="customActivity-page-dialog-status-badge"
                    >
                      <span className={styles["status-dot"]}></span>
                      {selectedItem.isActive ? "Active" : "Inactive"}
                    </div>
                  </div>
                </div>
              </section>

              <section className={styles.codesSection} data-testid="customActivity-page-dialog-codes-section">
                <h3 className={styles.sectionTitle}>
                  <i className="fa-solid fa-code"></i> Activity Codes By Type
                </h3>
                <div className={styles.typesContainer} data-testid="customActivity-page-dialog-types-container">
                  {[
                    { type: "CPT", codes: selectedItem.cptCodes },
                    { type: "HCPCS", codes: selectedItem.hecpcsCodes },
                    { type: "Dental", codes: selectedItem.dentalCodes },
                    { type: "Service", codes: selectedItem.serviceCodes },
                  ].map(
                    ({ type, codes }) =>
                      codes && codes.length > 0 && (
                        <div
                          key={type}
                          className={styles.typeGroup}
                          data-testid={`customActivity-page-dialog-type-group-${type.toLowerCase()}`}
                        >
                          <div className={styles.typeHeader}>
                            <i className="fa-solid fa-stethoscope"></i>
                            <span className={styles.typeName}>{type}</span>
                            <span
                              className={styles.typeCount}
                              data-testid={`customActivity-page-dialog-type-count-${type.toLowerCase()}`}
                            >
                              {codes.length}
                            </span>
                          </div>
                          <div className={styles.codesGrid}>
                            {codes.map((code, idx) => (
                              <span
                                key={idx}
                                className={styles.codePill}
                                data-testid={`customActivity-page-dialog-code-pill-${type.toLowerCase()}-${idx}`}
                              >
                                {code}
                              </span>
                            ))}
                          </div>
                        </div>
                      ),
                  )}
                  {!selectedItem.cptCodes?.length &&
                    !selectedItem.hecpcsCodes?.length &&
                    !selectedItem.dentalCodes?.length &&
                    !selectedItem.serviceCodes?.length && (
                      <div className={styles.emptyState} data-testid="customActivity-page-dialog-codes-empty">
                        <i className="fa-solid fa-info-circle"></i> No activity codes available
                      </div>
                    )}
                </div>

                {/* ── Audit Trail / Logs ── */}
                <div style={{ padding: "20px 28px 8px" }} data-testid="customActivity-page-dialog-audit-trail">
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                      <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                    <div
                      data-testid="customActivity-page-dialog-audit-createdby"
                      style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}
                    >
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created By</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>
                          {String(getCreatorName(selectedItem) ?? "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{getCreatorName(selectedItem) || "—"}</span>
                      </div>
                    </div>

                    <div
                      data-testid="customActivity-page-dialog-audit-createdat"
                      style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}
                    >
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created On</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).createdAt
                            ? new Date((selectedItem as any).createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                            : "—"}
                        </span>
                      </div>
                    </div>

                    <div
                      data-testid="customActivity-page-dialog-audit-updatedat"
                      style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}
                    >
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last Updated</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).updatedAt
                            ? new Date((selectedItem as any).updatedAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                            : "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {(selectedItem as any).auditLog?.length > 0 && (
                    <div
                      data-testid="customActivity-page-dialog-audit-history"
                      style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                        <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Update History</span>
                        <span
                          data-testid="customActivity-page-dialog-audit-count-badge"
                          style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}
                        >
                          {(selectedItem as any).auditLog.length} update{(selectedItem as any).auditLog.length !== 1 ? "s" : ""}
                        </span>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {(selectedItem as any).auditLog
                          .slice(0, showAllLogs ? undefined : 4)
                          .map((log: any, i: number) => (
                            <div
                              key={log.id}
                              data-testid={`customActivity-page-dialog-audit-item-${i}`}
                              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff" }}>
                                  {String(log.actorName ?? "?")[0].toUpperCase()}
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{log.actorName}</span>
                                {i === 0 && (
                                  <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>
                                    Latest
                                  </span>
                                )}
                              </div>
                              <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                                {new Date(log.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                          ))}

                        {(selectedItem as any).auditLog.length > 4 && (
                          <div style={{ textAlign: "center", padding: "6px 0" }}>
                            <button
                              onClick={() => setShowAllLogs((v) => !v)}
                              data-testid="customActivity-page-dialog-audit-showmore-btn"
                              style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}
                            >
                              {showAllLogs ? "Show less ↑" : `+${(selectedItem as any).auditLog.length - 4} more updates ↓`}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>

            <div className={styles["dialog-footer"]} data-testid="customActivity-page-dialog-footer">
              <button
                className={styles["btn-secondary"]}
                onClick={handleCloseDialog}
                data-testid="customActivity-page-dialog-footer-close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* Activity Details Dialog */}
      <ActivityDetailsDialog
        open={CptDialogOpen}
        onOpenChange={setCptDialogOpen}
        currentOpenItem={currentOpenItem}
        search={search}
        totalCount={totalCount}
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            clear();
            setEditIndex(null);
          }
          setIsDialogOpen(open);
        }}
      >
        <DialogContent className={styles.dialogContent} data-testid="customActivity-page-dialog-content">
          <DialogHeader className={styles.dialogHeader}>
            <DialogTitle className={styles.dialogTitle} data-testid="customActivity-page-form-dialog-title">
              {editIndex === null ? "Add Custom Activity Group" : "Edit Custom Activity Group"}
            </DialogTitle>
            <DialogDescription className={styles.dialogDescription} data-testid="customActivity-page-form-dialog-desc">
              Fill in the Details Below To {editIndex === null ? "create" : "update"} A Custom Activity Group.
            </DialogDescription>
          </DialogHeader>
          <div className={styles.dialogBody} data-testid="customActivity-page-form-dialog-body">
            <div className={styles.formGrid}>
              <div>
                <div className={styles.formGroup} data-testid="customActivity-page-input-groupname-group">
                  <Label htmlFor="name">Name :</Label>
                  <Input
                    id="name"
                    placeholder="Enter name..."
                    value={values.name}
                    onChange={(e) => setField("name", e.target.value)}
                    data-testid="customActivity-page-form-name-input"
                  />
                  {submitted && errors.name && (
                    <span className={styles.error} data-testid="customActivity-page-form-name-error">
                      {errors.name}
                    </span>
                  )}
                </div>
                <div className={styles.formGroup} data-testid="customActivity-page-input-activitytype-group">
                  <Label className={styles.label}>Activity Type</Label>
                  <div data-testid="customActivity-page-form-activitytype-selector">
                    <ActivityTypeSingleSelector value={selectedType} onChange={(val) => setSelectedType(val)} />
                  </div>
                </div>
                <div className={styles.formGroup} data-testid="customActivity-page-input-customcptgroup-group">
                  <Label className={styles.label} htmlFor="customCptCodes">Custom Activity Group</Label>
                  <div style={{ maxWidth: 500 }} data-testid="customActivity-page-form-custom-groups-select">
                    <CodeMultiSelect
                      fetchOptions={fetchCustomCodes}
                      value={customCptGroups}
                      onChange={setcustomCptGroups}
                      placeholder="Type at least 3 letters to search..."
                    />
                  </div>
                </div>
              </div>
              <div>
                <div className={styles.formGroup} data-testid="customActivity-page-input-description-group">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    placeholder=" Description..."
                    value={descriptions}
                    onChange={(e) => setDescriptions(e.target.value)}
                    data-testid="customActivity-page-form-description-input"
                  />
                </div>
                {selectedType && (
                  <div className={styles.formGroup} data-testid="customActivity-page-input-activitycodes-group">
                    <Label>{selectedType} Codes</Label>
                    <div style={{ maxWidth: 500 }} data-testid="customActivity-page-form-codes-select">
                      <CodeMultiSelect
                        fetchOptions={fetchCodesByType}
                        value={getCodesState().value}
                        onChange={getCodesState().setter}
                        placeholder={`Search ${selectedType} codes...`}
                         minChars={selectedType === "Service" ? 1 : 3}
                      />
                    </div>
                  </div>
                )}
                <div className={styles.formGroup} data-testid="customActivity-page-input-medicalcptgroup-group">
                  <Label className={styles.label}>Medical Activity Group</Label>
                  <div data-testid="customActivity-page-form-medical-groups-select">
                    <CodeMultiSelect
                      fetchOptions={fetchMedicalCodes}
                      value={medicalCptGroups}
                      onChange={setMedicalCptGroups}
                      placeholder="Type at least 3 letters to search..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className={styles.dialogFooter}>
            <Button
              onClick={() => (editIndex === null ? handleSubmit() : updateItem(editIndex))}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="customActivity-page-form-submit-btn"
            >
              {editIndex === null ? "Add Group" : "Update Group"}
            </Button>
            <Button
              className={styles.cancel}
              variant="outline"
              data-testid="customActivity-page-form-cancel-btn"
              onClick={() => {
                setIsDialogOpen(false);
                setEditIndex(null);
                setSubmitted(false);
                clear();
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warning Dialog */}
      <Dialog
        open={warningDialog.open}
        onOpenChange={(open) =>
          !open &&
          setWarningDialog({
            open: false,
            type: null,
            item: null,
            usageData: null,
            loadingUsage: false,
          })
        }
      >
        <DialogContent
          className="max-w-md bg-white p-0 overflow-hidden rounded-2xl flex flex-col max-h-[90vh]"
          data-testid="customActivity-page-warning-dialog-content"
        >
          <div className="p-6 pb-2 flex-shrink-0">
            <DialogHeader>
              <DialogTitle
                data-testid="customActivity-page-warning-dialog-title"
                className={`flex items-center gap-2 font-bold ${
                  warningDialog.type === "delete" || warningDialog.type === "hardDelete"
                    ? "text-red-600"
                    : "text-amber-600"
                }`}
              >
                <AlertTriangle className="h-5 w-5" />
                {warningDialog.type === "delete"
                  ? "Confirm deletion"
                  : warningDialog.type === "hardDelete"
                  ? "Permanent deletion"
                  : "Warning: inactive groups selected"}
              </DialogTitle>
              <DialogDescription
                data-testid="customActivity-page-warning-dialog-desc"
                className="text-slate-500 text-sm"
              >
                {warningDialog.type === "delete"
                  ? "Are you sure you want to delete this group? This action cannot be undone."
                  : warningDialog.type === "hardDelete"
                  ? "This will permanently remove the group from the system. This action cannot be undone."
                  : "Some selected groups are currently inactive or deleted. Do you still want to proceed?"}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div
            className="px-6 flex-1 min-h-0 flex flex-col overflow-y-auto"
            data-testid="customActivity-page-warning-dialog-body"
          >
            {/* Hard Delete Warning */}
            {warningDialog.type === "hardDelete" && (
              <div
                className="py-6 flex flex-col items-center justify-center gap-3"
                data-testid="customActivity-page-warning-hard-delete-info"
              >
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center border-2 border-red-100">
                  <i className="fa-solid fa-trash-can text-red-500 text-xl"></i>
                </div>
                <p className="text-sm text-slate-600 text-center px-4">
                  The group{" "}
                  <strong className="text-slate-800">"{warningDialog.item?.name}"</strong>{" "}
                  will be permanently deleted. This cannot be undone.
                </p>
              </div>
            )}

            {/* Update Warning */}
            {warningDialog.type === "update" && (
              <div
                className="space-y-3 mb-4"
                data-testid="customActivity-page-warning-update-info"
              >
                <p className="text-xs font-semibold text-slate-500 tracking-wider">
                  Inactive or deleted groups:
                </p>
                {(warningDialog.item as any)?.allInactive?.map((group: any, idx: number) => (
                  <div
                    key={idx}
                    data-testid={`customActivity-page-warning-inactive-item-${idx}`}
                    className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg"
                  >
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-slate-400 tracking-tight">
                        {group.groupType} Group
                      </span>
                      <span className={`text-sm font-medium ${group.isSoftDeleted ? "line-through text-slate-400" : "text-slate-700"}`}>
                        {group.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {group.isSoftDeleted ? (
                        <div className="flex items-center gap-1.5 text-red-500 bg-red-50 px-2 py-1 rounded-md border border-red-100">
                          <i className="fa-solid fa-trash-can text-[10px]"></i>
                          <span className="text-[10px] font-bold">Deleted</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-amber-500 bg-amber-50 px-2 py-1 rounded-md border border-amber-100">
                          <i className="fa-solid fa-eye-slash text-[10px]"></i>
                          <span className="text-[10px] font-bold">Inactive</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <p className="text-xs text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-100 mt-4 italic">
                  * Note: these groups are currently disabled or soft-deleted in the system.
                </p>
              </div>
            )}

            {/* Delete / Toggle Warning */}
            {warningDialog.type !== "update" && warningDialog.type !== "hardDelete" && (
              warningDialog.usageData?.isUsed || warningDialog.usageData?.totalUsageCount > 0 ? (
                <div
                  className={styles.alertContainer}
                  data-testid="customActivity-page-warning-usage-container"
                >
                  <div className="rounded-lg bg-amber-50 p-3 border border-amber-100 flex-shrink-0">
                    <p className="text-xs text-amber-800 font-medium text-center">
                      Currently active in{" "}
                      <strong>{warningDialog.usageData.totalUsageCount}</strong> location(s).
                    </p>
                  </div>
                  <div className={styles.alertScrollArea}>
                    {warningDialog.usageData.icdValidationRules?.length > 0 && (
                      <div
                        className={styles.alertSection}
                        data-testid="customActivity-page-warning-icd-rules"
                      >
                        <h4 className={styles.alertSectionTitle}>ICD Rules</h4>
                        {warningDialog.usageData.icdValidationRules.map((rule: any, idx: number) => (
                          <div
                            key={rule.ruleId || idx}
                            data-testid={`customActivity-page-warning-icd-rule-${idx}`}
                            className={styles.alertCard}
                          >
                            <span className="text-sm font-medium">{rule.ruleName}</span>
                            <span className={styles.alertBadge}>{formatText(rule.ruleType)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {warningDialog.usageData.activityValidationRules?.length > 0 && (
                      <div
                        className={styles.alertSection}
                        data-testid="customActivity-page-warning-activity-rules"
                      >
                        <h4 className={styles.alertSectionTitle}>Activity Rules</h4>
                        {warningDialog.usageData.activityValidationRules.map((rule: any, idx: number) => (
                          <div
                            key={rule.ruleId || idx}
                            data-testid={`customActivity-page-warning-activity-rule-${idx}`}
                            className={styles.alertActivityCard}
                          >
                            <span className="text-sm font-medium">{rule.ruleName}</span>
                            <span className={styles.alertActivityBadge}>{formatText(rule.ruleType)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {warningDialog.usageData.secondaryActivityValidationRules?.length > 0 && (
                      <div
                        className={styles.alertSection}
                        data-testid="customActivity-page-warning-secondary-activity-rules"
                      >
                        <h4 className={styles.alertSectionTitle}>Activity Two</h4>
                        {warningDialog.usageData.secondaryActivityValidationRules.map((rule: any, idx: number) => (
                          <div
                            key={rule.ruleId || idx}
                            data-testid={`customActivity-page-warning-secondary-rule-${idx}`}
                            className={styles.alertActivityCard}
                          >
                            <span className="text-sm font-medium">{rule.ruleName}</span>
                            <span className={styles.alertActivityBadge}>{formatText(rule.ruleType)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {warningDialog.usageData.relatedByGroups?.length > 0 && (
                      <div
                        className={styles.alertSection}
                        data-testid="customActivity-page-warning-related-groups"
                      >
                        <h4 className={styles.alertSectionTitle}>Related Groups</h4>
                        {warningDialog.usageData.relatedByGroups.map((group: any, idx: number) => (
                          <div
                            key={group.groupId || group.id || idx}
                            data-testid={`customActivity-page-warning-related-group-${idx}`}
                            className={styles.alertCustomGroupContainer}
                          >
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                              <span>Group: <strong>{group.groupName || group.name}</strong></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className={styles.alertFooterQuestion}>Do you still want to proceed?</p>
                </div>
              ) : (
                <div
                  className="py-10 text-center flex flex-col items-center justify-center"
                  data-testid="customActivity-page-warning-standard-confirm"
                >
                  <div className="bg-slate-50 p-4 rounded-full mb-4">
                    <AlertTriangle className="h-8 w-8 text-slate-300" />
                  </div>
                  <p className="text-slate-600 text-sm px-4">
                    Confirm to proceed with{" "}
                    {warningDialog.type === "delete" ? "deletion" : "deactivation"}.
                  </p>
                </div>
              )
            )}
          </div>

          <div className="p-6 pt-4 flex-shrink-0 bg-white border-t">
            <DialogFooter className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl h-10 text-sm"
                data-testid="customActivity-page-warning-cancel-btn"
                onClick={() => setWarningDialog((prev) => ({ ...prev, open: false }))}
              >
                Cancel
              </Button>
              <Button
                data-testid="customActivity-page-warning-confirm-btn"
                className={`flex-1 rounded-xl h-10 text-white font-bold text-sm ${
                  warningDialog.type === "delete" || warningDialog.type === "hardDelete"
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-amber-500 hover:bg-amber-600"
                }`}
                onClick={() => {
                  const itemId = (warningDialog.item as any)?.id;
                  if (warningDialog.type === "delete") confirmDelete(itemId);
                  else if (warningDialog.type === "hardDelete") hardDeleteItem(itemId);
                  else if (warningDialog.type === "update") executeActivityUpdate(itemId);
                  else confirmToggle(warningDialog.item!);
                  setWarningDialog((prev) => ({ ...prev, open: false }));
                }}
              >
                {warningDialog.type === "delete" ? "Yes, Delete"
                  : warningDialog.type === "hardDelete" ? "Yes, Permanently Delete"
                  : "Yes, Proceed"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster data-testid="customActivity-page-toaster" />

      <div className={styles.tableWrapper} data-testid="customActivity-page-table-wrapper">
        <DataTable
          onAdd={handleAdd}
          onEdit={(item: any) => {
            if (item.isSoftDeleted) return;
            handleEdit(item);
          }}
          onDelete={(id) => {
            const item = allItems.find((i) => i.id === id);
            if (item?.isSoftDeleted) return;
            removeItem(id);
          }}
          data={allItems}
          columns={columns}
          onDownload={handleDownload}
          addButtonLabel="Add New Group"
          pageIndex={page - 1}
          pageSize={limit}
          totalPages={totalPages}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(size) => {
            setLimit(size);
            setPage(1);
          }}
          globalSearch={searchInput}
          onGlobalSearchChange={setSearchInput}
          recoveryActions={(item: any) => {
            if (!canRestoreOrHardDelete()) {
              return (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-200 px-2 py-1 rounded-lg"
                  data-testid={`customActivity-table-action-locked-${item.id}`}
                >
                  <i className="fa-solid fa-lock text-[15px]"></i>
                </span>
              );
            }
            return (
              <div
                className="flex items-center justify-center gap-1.5"
                data-testid={`customActivity-table-recovery-actions-${item.id}`}
              >
                <button
                  onClick={() => restoreItem(item.id)}
                  data-testid={`customActivity-table-action-restore-btn-${item.id}`}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-all"
                >
                  <i className="fa-solid fa-rotate-left text-[9px]"></i>
                  Restore
                </button>
                <button
                  onClick={() => setWarningDialog({
                    open: true,
                    type: "hardDelete",
                    item,
                    usageData: null,
                    loadingUsage: false,
                  })}
                  data-testid={`customActivity-table-action-harddelete-btn-${item.id}`}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-all"
                >
                  <i className="fa-solid fa-trash text-[9px]"></i>
                  Delete
                </button>
              </div>
            );
          }}
          data-testid="customActivity-page-datatable"
        />
      </div>
    </div>
  );
};

export default CustomActivity;